import { useCallback, useMemo } from 'react'
import { useIntl, type IntlShape } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'

import BodyText from 'LuxKit/Typography/BodyText'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import Heading from 'LuxKit/Typography/Heading'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import LineAngleUpIcon from 'LuxKit/Icons/line/LineAngleUpIcon'
import SolidBedIcon from 'LuxKit/Icons/solid/SolidBedIcon'
import ImageCarousel from 'LuxKit/Carousel/ImageCarousel'
import AddHotelToTripButton from 'tripPlanner/components/AddToTrip/AddHotelToTripButton'
import type { SendMessageOptions } from 'tripPlanner/components/AddToTrip/useAddHotelToTrip'
import type {
  HotelRoomOptionSelection,
  HotelRoomOptionsResult,
  TripItineraryData,
} from 'tripPlanner/api/types'
import ChatHotelRoomOptionsRateOption from './ChatHotelRoomOptionsRateOption'

const RoomSection = styled.div`
  padding: ${rem(16)};
`

const RoomHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};
  margin-bottom: ${rem(12)};
`

const RoomImageFrame = styled.div`
  width: 100%;
  aspect-ratio: 16 / 9;
  max-height: ${rem(260)};
  border-radius: ${props => props.theme.borderRadius.L};
  overflow: hidden;
  background-color: ${props => props.theme.palette.neutral.default.six};
  display: flex;
  align-items: center;
  justify-content: center;
`

const RoomImageCarousel = styled(ImageCarousel)`
  height: 100%;
  width: 100%;
  border-radius: ${props => props.theme.borderRadius.L};
  overflow: hidden;
`

const RateList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
  margin-top: ${rem(12)};
`

const RateRow = styled.div`
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  border-radius: ${props => props.theme.borderRadius.L};
  padding: ${rem(14)};
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
`

const PricingUnavailableRow = styled(RateRow)`
  background-color: ${props => props.theme.palette.neutral.default.seven};
`

const PricingUnavailableContent = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${rem(12)};
  align-items: start;

  @container (min-width: ${rem(520)}) {
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
  }
`

const RateFooterAction = styled.div`
  justify-self: start;

  @container (min-width: ${rem(520)}) {
    justify-self: end;
  }
`

const PackageToggleRow = styled.button`
  width: 100%;
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  border-radius: ${props => props.theme.borderRadius.L};
  padding: ${rem(14)};
  background-color: ${props => props.theme.palette.neutral.default.seven};
  color: ${props => props.theme.palette.neutral.default.one};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${rem(12)};
  cursor: pointer;
  text-align: start;
`

const ROOM_IMAGE_PARAMS = {
  width: 760,
  height: 428,
  fit: 'center' as const,
  gravity: 'auto' as const,
  objectFit: 'cover' as const,
}

function getPackageToggleLabel(
  count: number,
  packagesCollapsed: boolean,
  intl: IntlShape,
): string {
  if (packagesCollapsed) {
    return intl.formatMessage({
      id: 'idri7S_st0KMLoj8WfVL',
      defaultMessage: 'Show {count, plural, one {# package option} other {# package options}} for this room.',
      description: 'Collapsed package options row in trip planner chat hotel room options',
    }, { count })
  }

  return intl.formatMessage({
    id: 'b_k7e8DpjnuAY5XJp5xw',
    defaultMessage: 'Hide package options for this room.',
    description: 'Expanded package options row in trip planner chat hotel room options',
  })
}

function isRoomOnlySelection(selection: HotelRoomOptionSelection): boolean {
  return !!selection.roomTypeId && !selection.packageId && !selection.roomRateId
}

function isRateSelected(
  rate: HotelRoomOptionsResult['roomOptions'][number]['rates'][number],
  selections: Array<HotelRoomOptionSelection>,
): boolean {
  return selections.some((selection) =>
    (!!selection.packageId && selection.packageId === rate.packageId) ||
    (!!selection.roomRateId && selection.roomRateId === rate.roomRateId),
  )
}

function shouldDefaultCollapseRoom(
  room: HotelRoomOptionsResult['roomOptions'][number],
  selections: Array<HotelRoomOptionSelection>,
): boolean {
  return selections.some((selection) => isRoomOnlySelection(selection) && selection.roomTypeId === room.roomTypeId)
}

function RoomImageGallery({
  imageIds,
}: {
  imageIds: Array<string>
}) {
  const images = useMemo(() => imageIds.map((id) => ({ id })), [imageIds])

  if (images.length === 0) {
    return <SolidBedIcon size="L" colour="neutral-three" />
  }

  return (
    <RoomImageCarousel
      images={images}
      imageParams={ROOM_IMAGE_PARAMS}
      arrowBehaviour={images.length > 1 ? 'interaction-visible' : 'hidden'}
      disablePlaceholders
    />
  )
}

interface Props {
  room: HotelRoomOptionsResult['roomOptions'][number]
  result: HotelRoomOptionsResult
  selections: Array<HotelRoomOptionSelection>
  packagesCollapsed: boolean | undefined
  pricingUnavailableMessage: string
  itineraryData?: TripItineraryData
  onSendMessage?: (message: string, options?: SendMessageOptions) => void
  onPackagesCollapsedChange: (roomTypeId: string, nextCollapsed: boolean) => void
}

function ChatHotelRoomOptionsRoomCard({
  room,
  result,
  selections,
  packagesCollapsed: packagesCollapsedOverride,
  pricingUnavailableMessage,
  itineraryData,
  onSendMessage,
  onPackagesCollapsedChange,
}: Props) {
  const intl = useIntl()
  const packagesCollapsed = packagesCollapsedOverride ?? shouldDefaultCollapseRoom(room, selections)
  const packageToggleLabel = getPackageToggleLabel(room.rates.length, packagesCollapsed, intl)
  const handlePackageToggle = useCallback(() => {
    onPackagesCollapsedChange(room.roomTypeId, !packagesCollapsed)
  }, [onPackagesCollapsedChange, packagesCollapsed, room.roomTypeId])

  let rateContent = (
    <PricingUnavailableRow>
      <PricingUnavailableContent>
        <BodyTextBlock
          variant="medium"
          weight="bold"
          startIcon={<LineInfoCircleIcon size="XS" />}
        >
          {pricingUnavailableMessage}
        </BodyTextBlock>
        <RateFooterAction>
          {onSendMessage && (
            <AddHotelToTripButton
              itineraryData={itineraryData}
              hotelName={result.hotelName}
              offerId={result.offerId}
              onSendMessage={onSendMessage}
              filterNights={result.nights}
              checkIn={result.checkIn}
              checkOut={result.checkOut}
              variant="outline"
              size="small"
            />
          )}
        </RateFooterAction>
      </PricingUnavailableContent>
    </PricingUnavailableRow>
  )

  if (room.rates.length > 0) {
    rateContent = (
      <>
        <PackageToggleRow
          type="button"
          onClick={handlePackageToggle}
          aria-expanded={!packagesCollapsed}
        >
          <BodyTextBlock
            variant="medium"
            weight="bold"
            startIcon={<LineInfoCircleIcon size="XS" />}
          >
            {packageToggleLabel}
          </BodyTextBlock>
          {packagesCollapsed ? <LineAngleDownIcon size="S" /> : <LineAngleUpIcon size="S" />}
        </PackageToggleRow>
        {!packagesCollapsed && room.rates.map((rate, index) => (
          <ChatHotelRoomOptionsRateOption
            key={`${rate.packageId ?? rate.packageName ?? 'package'}-${rate.roomRateId ?? rate.rateName ?? `rate-${index}`}`}
            rate={rate}
            room={room}
            result={result}
            itineraryData={itineraryData}
            onSendMessage={onSendMessage}
            currencyCode={result.currencyCode}
            pricingMode={result.pricingMode}
            nights={result.nights}
            checkIn={result.checkIn}
            isRoomOptionSelected={isRateSelected(rate, selections)}
          />
        ))}
      </>
    )
  }

  return (
    <RoomSection>
      <RoomHeader>
        <Heading variant="heading6">{room.roomName}</Heading>
        {room.bedGroupDescriptions.length > 0 && (
          <BodyText variant="small" colour="neutral-three">
            {room.bedGroupDescriptions.slice(0, 2).join(' · ')}
          </BodyText>
        )}
      </RoomHeader>
      <RoomImageFrame>
        <RoomImageGallery imageIds={room.imageIds} />
      </RoomImageFrame>
      <RateList>
        {rateContent}
      </RateList>
    </RoomSection>
  )
}

export default ChatHotelRoomOptionsRoomCard
